/**
 * Unsent reply drafts, kept in `localStorage` per ticket.
 *
 * The ticket page already goes to some length to protect a half-written reply from the
 * poll — the render query is never refetched behind the agent, and a send is blocked
 * server-side rather than merged. All of that guards the draft against OTHER PEOPLE and
 * none of it against the agent's own browser: a reload, a closed tab, or a click through to
 * the queue to check something loses minutes of writing with no way back. The reply box is
 * the one thing on that page that exists nowhere else until it is sent.
 *
 * Deliberately `localStorage` and not the API: a draft is not a fact about the ticket, and
 * persisting it server-side would make it visible to whoever opens the ticket next, which is
 * the opposite of what an unfinished reply wants.
 */

/** A stored draft. `savedAt` is what lets the page say how old the thing it restored is. */
export interface SupportDraft {
  readonly body: string;
  readonly isInternal: boolean;
  readonly savedAt: string;
}

const KEY_PREFIX = 'support-draft:';

/**
 * How long a draft survives.
 *
 * A fortnight, because the failure this guards against is a browser restart, not an
 * archive. Restoring a three-month-old reply into a ticket that has moved on since is worse
 * than losing it — the agent would be editing an answer to a conversation that no longer
 * exists, and the state-token guard would only catch it at the moment of sending.
 */
const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

/** The storage key for one ticket. */
function keyFor(ticketId: string): string {
  return `${KEY_PREFIX}${ticketId}`;
}

/**
 * Reads a stored draft back, or null when there is nothing usable.
 *
 * Every failure mode collapses to null on purpose — no storage at all (private mode, a
 * hardened browser), a value another version wrote, a value hand-edited into nonsense. A
 * draft is a convenience; it may never be the reason the ticket page fails to render.
 *
 * @param ticketId - The ticket whose draft to restore.
 */
export function readDraft(ticketId: string): SupportDraft | null {
  try {
    const raw = window.localStorage.getItem(keyFor(ticketId));
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;

    const { body, isInternal, savedAt } = parsed as Record<string, unknown>;
    if (typeof body !== 'string' || body.trim().length === 0) return null;
    if (typeof savedAt !== 'string') return null;

    const age = Date.now() - new Date(savedAt).getTime();
    if (!Number.isFinite(age) || age > MAX_AGE_MS) {
      clearDraft(ticketId);
      return null;
    }

    return { body, isInternal: isInternal === true, savedAt };
  } catch {
    return null;
  }
}

/**
 * Stores a draft, or removes it once the box is empty again.
 *
 * An empty body clears rather than storing an empty draft, so an agent who deletes what
 * they wrote does not get it offered back on the next visit.
 *
 * @param ticketId - The ticket being written to.
 * @param draft - The composer's current contents.
 */
export function writeDraft(
  ticketId: string,
  draft: { body: string; isInternal: boolean },
): void {
  if (draft.body.trim().length === 0) {
    clearDraft(ticketId);
    return;
  }
  try {
    // An unchanged draft keeps the timestamp it already had. Merely OPENING the ticket writes
    // once, so re-stamping would restart the 14-day clock on every visit and a draft nobody
    // has touched since spring would still be offered back.
    const existing = readDraft(ticketId);
    const unchanged =
      existing !== null &&
      existing.body === draft.body &&
      existing.isInternal === draft.isInternal;

    window.localStorage.setItem(
      keyFor(ticketId),
      JSON.stringify({
        ...draft,
        savedAt: unchanged ? existing.savedAt : new Date().toISOString(),
      }),
    );
  } catch {
    // Quota exceeded, or storage disabled entirely. Nothing to tell the agent: the draft is
    // still on screen and still sendable, it just will not survive a reload.
  }
}

/** Removes a ticket's draft. Called after a successful send. */
export function clearDraft(ticketId: string): void {
  try {
    window.localStorage.removeItem(keyFor(ticketId));
  } catch {
    // See writeDraft.
  }
}
